import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CFormSelect,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CCard,
  CCardHeader,
  CCardBody,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilInfo, cilMediaPlay, cilList, cilFilter, cilTrash } from "@coreui/icons";
import dayjs from "dayjs";
import { getFakeLocalDate, formatDuration } from "../utils/timeUtils";

const CameraExtraModals = ({
  sessionsModalVisible,
  setSessionsModalVisible,
  selectedTruckData,
  sessionSortBy,
  setSessionSortBy,
  sortedSessions,
  schedule,
  setSelectedSessionInfo,
  setInfoModalVisible,
  handleTimeDoubleClick,
  infoModalVisible,
  selectedSessionInfo,
  liveImageModalVisible,
  setLiveImageModalVisible,
  liveData,
  device,
  trucksList,
  refreshAnalytics,
}) => {
  const [updatingSessionId, setUpdatingSessionId] = useState(null);

  const handleTruckChange = async (sessionId, newTruckId) => {
    if (!sessionId) {
      toast.error("Ошибка: не найден ID заезда.");
      return;
    }
    setUpdatingSessionId(sessionId);
    try {
      const token = localStorage.getItem("token");

      const isFalsePositive = newTruckId === "no_arrival";
      const finalTruckId = isFalsePositive ? null : (newTruckId || null);

      await axios.patch(`/api/analytics/sessions/${sessionId}/details/`, {
        truck_id: finalTruckId,
        is_false_positive: isFalsePositive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Изменения успешно сохранены!");
      setSessionsModalVisible(false);
      if (refreshAnalytics) refreshAnalytics();
    } catch (error) {
      toast.error("Ошибка при сохранении.");
    } finally {
      setUpdatingSessionId(null);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm(`Вы уверены, что хотите удалить заезд #${sessionId}?`)) {
      return;
    }
    setUpdatingSessionId(sessionId);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/analytics/sessions/${sessionId}/details/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Заезд #${sessionId} успешно удален`);
      setSessionsModalVisible(false);
      if (refreshAnalytics) refreshAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.error || "Ошибка при удалении заезда");
    } finally {
      setUpdatingSessionId(null);
    }
  };

  const getMethodBadge = (method) => {
    switch (method) {
      case 'manual':
        return <span className="badge border border-warning text-warning bg-warning bg-opacity-10 fw-bold px-2 py-1">🛠️ Вручную</span>;
      case 'gps':
        return <span className="badge border border-info text-info bg-info bg-opacity-10 fw-bold px-2 py-1">📡 GPS / Omnicomm</span>;
      case 'ble':
        return <span className="badge border border-success text-success bg-success bg-opacity-10 fw-bold px-2 py-1">📶 BLE Радио</span>;
      default:
        return <span className="badge border border-secondary text-secondary bg-secondary bg-opacity-10 fw-bold px-2 py-1">❓ Нет метки</span>;
    }
  };

  return (
    <>
      {/* --- МОДАЛЬНОЕ ОКНО ЖУРНАЛА ЗАЕЗДОВ --- */}
      <CModal
        visible={sessionsModalVisible}
        onClose={() => setSessionsModalVisible(false)}
        alignment="center"
        size="xl"
        className="modal-wide-sessions"
      >
        <CModalHeader className="bg-light border-bottom-0 pb-3">
          <CModalTitle className="d-flex align-items-center fw-bold mb-0">
            <CIcon icon={cilList} className="me-2 text-primary" size="xl" />
            Журнал заездов: <span className="text-primary ms-2">{selectedTruckData.label}</span>
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="bg-light pt-0">
          {/* Информационная панель и фильтры */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center bg-white p-3 rounded shadow-sm border mb-3 gap-3">
            <div>
              <span className="text-muted small fw-bold text-uppercase d-block mb-1">Всего заездов в группе</span>
              <span className="fs-3 fw-bold text-dark lh-1">{selectedTruckData.sessions.length}</span>
            </div>

            <div className="d-flex align-items-center bg-light p-2 rounded border">
              <CIcon icon={cilFilter} className="text-muted me-2" />
              <span className="me-2 text-muted small fw-bold">Сортировка:</span>
              <CFormSelect
                size="sm"
                style={{ minWidth: "220px", cursor: "pointer" }}
                className="fw-semibold text-primary border-primary shadow-sm"
                value={sessionSortBy}
                onChange={(e) => setSessionSortBy(e.target.value)}
              >
                <option value="time_desc">По времени (новые сначала)</option>
                <option value="time_asc">По времени (старые сначала)</option>
                <option value="cycles_desc">По ковшам (больше сначала)</option>
                <option value="cycles_asc">По ковшам (меньше сначала)</option>
              </CFormSelect>
            </div>
          </div>

          {/* Таблица заездов */}
          <div className="table-responsive border rounded bg-white shadow-sm" style={{ maxHeight: "65vh", overflowY: "auto" }}>
            <CTable hover align="middle" className="mb-0 border-0 w-100" small>
              <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <CTableRow className="align-middle">
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 ps-3 pe-2 text-nowrap" style={{ width: "40px" }}>
                    #
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-start text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Время загрузки
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Ковшей
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Объем (м³)
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Масса (т)
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Длительность
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Источник метки
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 px-2 text-nowrap">
                    Переназначить машину
                  </CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-uppercase text-muted small py-2 pe-3 text-nowrap">
                    Действия
                  </CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {sortedSessions.map((sess, idx) => {
                  const fakeStart = getFakeLocalDate(sess.start, schedule.timezone_offset);
                  const fakeEnd = sess.end ? getFakeLocalDate(sess.end, schedule.timezone_offset) : fakeStart;
                  const durationSec = Math.max(0, dayjs(fakeEnd).diff(dayjs(fakeStart), 'second'));

                  return (
                    <CTableRow key={idx} className="border-bottom">
                      <CTableDataCell className="text-center text-muted small fw-bold ps-3 pe-2 py-2 text-nowrap">
                        {idx + 1}
                      </CTableDataCell>

                      <CTableDataCell className="text-start fw-semibold text-dark px-2 py-2 text-nowrap">
                        <div>{dayjs(fakeStart).format("DD.MM.YYYY")}</div>
                        <div className="small text-muted">{dayjs(fakeStart).format("HH:mm:ss")}</div>
                      </CTableDataCell>

                      <CTableDataCell className="text-center px-2 py-2 text-nowrap">
                        <span className={`badge ${sess.cycles >= 10 ? "bg-success" : "bg-primary"} rounded-pill fs-6 px-2 py-1 shadow-sm`}>
                          {sess.cycles}
                        </span>
                      </CTableDataCell>

                      <CTableDataCell className="text-center fw-bold text-success px-2 py-2 text-nowrap">
                        {Number(sess.vol || 0).toFixed(1)}
                      </CTableDataCell>

                      <CTableDataCell className="text-center fw-bold text-info px-2 py-2 text-nowrap">
                        {(Number(sess.mass || 0) / 1000).toFixed(1)}
                      </CTableDataCell>

                      <CTableDataCell className="text-center fw-bold text-dark px-2 py-2 text-nowrap">
                        {formatDuration(durationSec)}
                      </CTableDataCell>

                      <CTableDataCell className="text-center px-2 py-2 text-nowrap">
                        {getMethodBadge(sess.determination_method)}
                      </CTableDataCell>

                      <CTableDataCell className="text-center px-2 py-2 text-nowrap">
                        {updatingSessionId === sess.id ? (
                          <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        ) : (
                          <CFormSelect
                            size="sm"
                            value="default"
                            className="bg-light fw-semibold mx-auto"
                            onChange={(e) => {
                              if (e.target.value !== "default") {
                                handleTruckChange(sess.id, e.target.value);
                              }
                            }}
                            style={{ minWidth: "140px", maxWidth: "180px" }}
                          >
                            <option value="default" disabled>-- Изменить --</option>
                            <option value="">Неизвестная машина</option>
                            <option value="no_arrival" className="text-danger fw-bold">❌ Не было заезда</option>
                            <optgroup label="Ваш автопарк">
                              {trucksList && trucksList.map(t => (
                                <option key={t.id} value={t.id}>{t.brand} ({t.plate_number})</option>
                              ))}
                            </optgroup>
                          </CFormSelect>
                        )}
                      </CTableDataCell>

                      <CTableDataCell className="text-center pe-3 py-2 text-nowrap">
                        {/* Кнопка информации */}
                        <CButton
                          size="sm"
                          variant="ghost"
                          className="me-1 rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                          title="Детализация Bluetooth телеметрии"
                          style={{
                            color: '#8c00ff',
                            width: '28px',
                            height: '28px'
                          }}
                          onClick={() => {
                            setSelectedSessionInfo(sess);
                            setInfoModalVisible(true);
                          }}
                        >
                          <CIcon icon={cilInfo} size="lg" />
                        </CButton>

                        {/* Кнопка воспроизведения */}
                        <CButton
                          size="sm"
                          color="dark"
                          variant="outline"
                          className="fw-bold me-1 align-middle px-2 py-1"
                          style={{ borderRadius: "6px" }}
                          onClick={() => {
                            setSessionsModalVisible(false);
                            handleTimeDoubleClick({
                              exactTime: fakeStart.getTime(),
                              item: { label: `${selectedTruckData.label} (${sess.cycles} ковш.)` },
                              fromSessions: true
                            });
                          }}
                        >
                          <CIcon icon={cilMediaPlay} className="me-1" size="sm" /> Смотреть
                        </CButton>

                        {/* Кнопка удаления */}
                        <CButton
                          size="sm"
                          color="danger"
                          variant="ghost"
                          className="rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                          style={{ width: '28px', height: '28px' }}
                          title="Удалить заезд"
                          disabled={updatingSessionId === sess.id}
                          onClick={() => handleDeleteSession(sess.id)}
                        >
                          <CIcon icon={cilTrash} />
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}
                {sortedSessions.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan="9" className="text-center text-muted p-4">
                      Список заездов пуст
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>
        </CModalBody>
      </CModal>

      {/* --- МОДАЛЬНОЕ ОКНО ИНФОРМАЦИИ (BLE) --- */}
      <CModal
        visible={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        alignment="center"
        size="lg"
      >
        <CModalHeader className="bg-light">
          <CModalTitle className="fw-bold text-dark d-flex align-items-center">
            <CIcon icon={cilInfo} className="me-2" size="xl" style={{ color: '#8c00ff' }} />
            Детализация телеметрии и ИИ
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="bg-light p-4">
          {selectedSessionInfo && (
            <>
              {selectedSessionInfo.debug_ble_details?.length > 0 && (
                <div className="mb-4 d-flex flex-wrap gap-2">
                  {selectedSessionInfo.debug_ble_details.map((txt, i) => (
                    <span key={i} className="badge bg-secondary text-wrap text-start" style={{ lineHeight: "1.4", fontSize: "0.85rem" }}>
                      {txt}
                    </span>
                  ))}
                </div>
              )}

              {selectedSessionInfo.sub_sessions?.map((sub, idx) => (
                <CCard key={idx} className="mb-3 shadow-sm border-0">
                  <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                    <strong className="text-primary fs-5">Заезд #{idx + 1}</strong>
                    <span className="text-muted small fw-bold bg-light px-2 py-1 rounded border">
                      {dayjs(getFakeLocalDate(sub.start, schedule.timezone_offset)).format("HH:mm:ss")}
                    </span>
                  </CCardHeader>
                  <CCardBody className="py-3">
                    <div className="mb-3 border-bottom pb-3">
                      <span className="text-muted">Победитель (машина): </span>
                      <span className="fw-bold fs-6 text-dark ms-2">
                        {sub.winner || "Не удалось определить"}
                      </span>
                    </div>

                    <div className="fw-bold small text-muted text-uppercase mb-2">Кандидаты вокруг экскаватора:</div>
                    <ul className="list-group list-group-flush mb-0">
                      {Object.keys(sub.candidates || {}).length > 0 ? (
                        Object.entries(sub.candidates).map(([name, count]) => (
                          <li key={name} className="list-group-item d-flex justify-content-between align-items-center px-0 bg-transparent">
                            <span className="fw-bold text-dark">{name}</span>
                            <span className="badge bg-primary rounded-pill px-3 py-2">{count} пакетов связи</span>
                          </li>
                        ))
                      ) : (
                        <li className="list-group-item px-0 bg-transparent text-danger fw-bold">
                          Отсутствуют метки в радиусе видимости (Слепая зона)
                        </li>
                      )}
                    </ul>
                  </CCardBody>
                </CCard>
              ))}
            </>
          )}
        </CModalBody>
        <CModalFooter className="bg-light border-top-0 pt-0">
          <CButton color="secondary" className="px-4 fw-bold" onClick={() => setInfoModalVisible(false)}>
            Понятно
          </CButton>
        </CModalFooter>
      </CModal>

      {/* --- МОДАЛЬНОЕ ОКНО ПРЯМОЙ ТРАНСЛЯЦИИ (FullScreen) --- */}
      <CModal
        visible={liveImageModalVisible}
        onClose={() => setLiveImageModalVisible(false)}
        alignment="center"
        size="xl"
      >
        <CModalHeader closeButton className="bg-dark text-white border-bottom-0">
          <CModalTitle>Камера: {device?.name} (Онлайн)</CModalTitle>
        </CModalHeader>
        <CModalBody className="p-0 bg-black text-center d-flex align-items-center justify-content-center" style={{ minHeight: "50vh" }}>
          {liveData?.frame_url ? (
            <img
              src={`${liveData.frame_url}?t=${Date.now()}`}
              alt="Live stream fullscreen"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <div className="p-5 text-white">Изображение недоступно</div>
          )}
        </CModalBody>
      </CModal>
    </>
  );
};

export default CameraExtraModals;